/**
 * Public patient checkout — /buy/:slug?clinic=<clinic_user_id>
 *
 * Iter 84b end-to-end checkout wiring:
 *   1. Load the flagship program by slug (public, no auth).
 *   2. Patient picks a tier + enters email + name.
 *   3. POST /api/programs/checkout — backend creates Stripe Checkout session.
 *   4. window.location → Stripe-hosted checkout.
 *   5. On return → /checkout/success polls /api/programs/checkout/status until paid.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Repeat, ShieldCheck, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function formatPrice(cents, interval) {
  if (cents == null) return "—";
  const dollars = (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return interval ? `$${dollars}/${interval}` : `$${dollars}`;
}

export default function PublicCheckout() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const clinicId = params.get("clinic") || "";

  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTierId, setSelectedTierId] = useState("");
  const [email, setEmail] = useState("");
  const [patientName, setPatientName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/public/flagship-programs/${slug}`);
        if (!alive) return;
        setProgram(r.data);
        // Default-select the cheapest active tier.
        const sorted = [...(r.data.tiers || [])].sort(
          (a, b) => (a.patient_price_cents || 0) - (b.patient_price_cents || 0)
        );
        if (sorted.length) setSelectedTierId(sorted[0].id);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  const selectedTier = useMemo(
    () => program?.tiers?.find((t) => t.id === selectedTierId) || null,
    [program, selectedTierId]
  );

  const includedServices = useMemo(() => {
    if (!program || !selectedTier) return [];
    const byId = Object.fromEntries(program.services.map((s) => [s.id, s]));
    return (selectedTier.included_service_ids || [])
      .map((sid) => byId[sid])
      .filter(Boolean);
  }, [program, selectedTier]);

  const submit = async () => {
    if (!clinicId) {
      toast.error("This checkout link is missing the clinic. Please use the link from your clinic.");
      return;
    }
    if (!email.trim() || !patientName.trim() || !selectedTier) {
      toast.error("Please fill out your name + email and pick a tier.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/programs/checkout", {
        program_id: program.id,
        tier_id: selectedTier.id,
        clinic_id: clinicId,
        origin_url: window.location.origin,
        customer_email: email.trim(),
        patient_name: patientName.trim(),
      });
      // Off to Stripe-hosted Checkout.
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }
  if (!program) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center">
        <div data-testid="buy-not-found" className="text-center">
          <div className="text-2xl font-bold text-slate-100 mb-2">Program not found</div>
          <p className="text-sm text-slate-400">
            This checkout link is invalid or the program is no longer available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="buy-page">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-blue-400 mb-2">
          {program.compliance_flag === "counsel_required" ? "Cash-pay program" : "Health program"}
        </div>
        <h1 className="text-4xl font-bold mb-3" data-testid="buy-program-name">{program.name}</h1>
        <p className="text-base text-slate-400 max-w-2xl mb-10">
          {program.patient_facing_description || program.description}
        </p>

        <div className="grid lg:grid-cols-3 gap-4 mb-10">
          {program.tiers.map((tier) => {
            const active = tier.id === selectedTierId;
            return (
              <button
                key={tier.id}
                type="button"
                onClick={() => setSelectedTierId(tier.id)}
                data-testid={`buy-tier-${tier.id}`}
                className={`text-left rounded-2xl border p-5 transition ${
                  active
                    ? "border-blue-500 bg-blue-950/30 ring-2 ring-blue-500/40"
                    : "border-slate-800 bg-slate-900/40 hover:border-slate-700"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-bold text-slate-100">{tier.name}</div>
                  {tier.is_recurring && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                      <Repeat className="w-2.5 h-2.5" /> Recurring
                    </span>
                  )}
                </div>
                <div className="text-2xl font-bold text-slate-100 mb-2">
                  {formatPrice(tier.patient_price_cents, tier.is_recurring ? tier.recurring_interval : null)}
                </div>
                {tier.summary && (
                  <p className="text-xs text-slate-400 line-clamp-3">{tier.summary}</p>
                )}
                {tier.inclusions?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {tier.inclusions.slice(0, 4).map((inc, i) => (
                      <li key={i} className="text-[11px] text-slate-300 flex items-start gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-none mt-0.5" />
                        <span>{inc}</span>
                      </li>
                    ))}
                    {tier.inclusions.length > 4 && (
                      <li className="text-[11px] text-slate-500 pl-4">+ {tier.inclusions.length - 4} more</li>
                    )}
                  </ul>
                )}
              </button>
            );
          })}
        </div>

        {selectedTier && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="text-sm font-bold text-slate-100 mb-3">What's included in {selectedTier.name}</div>
              <ul className="space-y-2">
                {includedServices.map((svc) => (
                  <li
                    key={svc.id}
                    data-testid={`buy-included-svc-${svc.id}`}
                    className="rounded-lg border border-slate-800 bg-slate-900/60 p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-100">{svc.name}</div>
                      {svc.requires_prescription && (
                        <span className="text-[10px] text-amber-300 font-semibold uppercase">Rx</span>
                      )}
                    </div>
                    {svc.description && (
                      <p className="text-[11px] text-slate-400 mt-1">{svc.description}</p>
                    )}
                  </li>
                ))}
              </ul>
              {selectedTier.refund_rules && (
                <div className="mt-5 rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-[11px] text-slate-400">
                  <div className="font-semibold text-slate-300 mb-1">Refund & cancellation</div>
                  <div>{selectedTier.refund_rules}</div>
                  {selectedTier.cancellation_rules && <div className="mt-1">{selectedTier.cancellation_rules}</div>}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-blue-500/30 bg-blue-950/20 p-5 h-fit lg:sticky lg:top-6">
              <div className="text-[10px] uppercase tracking-wider font-bold text-blue-300 mb-1">
                Patient checkout
              </div>
              <div className="text-2xl font-bold text-slate-100 mb-4">
                {formatPrice(selectedTier.patient_price_cents, selectedTier.is_recurring ? selectedTier.recurring_interval : null)}
              </div>
              <div className="space-y-3 mb-4">
                <label className="block">
                  <span className="text-[10px] uppercase font-semibold text-slate-400">Your name</span>
                  <input
                    type="text"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    data-testid="buy-name-input"
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm text-slate-100"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase font-semibold text-slate-400">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="buy-email-input"
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm text-slate-100"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !selectedTier}
                data-testid="buy-checkout-button"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 text-white text-sm font-semibold py-2.5 hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {submitting ? "Redirecting…" : "Proceed to secure checkout"}
              </button>
              <div className="mt-3 flex items-center gap-1 text-[10px] text-slate-500">
                <ShieldCheck className="w-3 h-3" />
                Payments processed securely via Stripe
              </div>
              {!clinicId && (
                <div
                  data-testid="buy-missing-clinic-warning"
                  className="mt-3 rounded-md border border-rose-500/30 bg-rose-950/30 p-2 text-[11px] text-rose-200"
                >
                  This checkout link is missing the clinic. Please open it from your clinic's website.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
